/**
 * 品牌详情页面 — BrandDetail（参照 company-detail 概念稿结构模式：指标卡 + 基本信息 + 关联列表）。
 *
 * 信息架构：
 *   1. 指标卡：产品数 / AI 可见性均值 / 监测话题数（接口取不到的卡跳过，不留假数据）
 *   2. 基本信息卡（原"主体身份/联系信息/业务与内容"三卡合并为一张 kv 卡，减少滚动）
 *   3. 名下产品列表卡（DataTable，行点击进 /products/:id）
 *
 * 数据口径说明（不虚构接口）：
 *   - 产品数/名下产品来自 GET /api/products?brand_id=
 *   - AI 可见性均值 = GET /api/brands/{id}/ai-visibility 全部探测记录 mention_rate 均值
 *   - 监测话题数 = GET /api/ai-visibility/matrix?brand_id= 的 topics 数（端点未就绪则跳过该卡）
 *   - 各指标接口均为独立失败域，失败仅跳过对应卡片，不红屏
 *
 * 保留：批 1 EmptyState 兜底（无 id 引导）、面包屑、只读呈现（PRD 1.2）。
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import {
  Inventory2Outlined,
  VisibilityOutlined,
  TopicOutlined,
} from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import StatCard from "../components/StatCard";
import Badge from "../components/Badge";
import GhsRing from "../components/GhsRing";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import { getBrand, getBrandAiVisibility } from "../api/brands";
import { getProducts } from "../api/products";
import { getVisibilityMatrix } from "../api/visibility";
import { useCompanyStore } from "../store/companyStore";
import type { Brand, ProductListItem, PaginatedData } from "../types";

/** 字段行：标签 + 值。 */
function FieldRow({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          color: value ? "text.primary" : "text.disabled",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </Typography>
    </Grid>
  );
}

/** 信息卡内的小节标题（原三分组的弱分隔）。 */
function SectionLabel({ text }: { text: string }) {
  return (
    <Typography
      sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary", mb: 1, mt: 2 }}
    >
      {text}
    </Typography>
  );
}

export default function BrandDetail() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const effectiveBrandId = brandId || selectedBrandId;
  const [data, setData] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 概念稿新增区块：名下产品 / AI 可见性均值 / 监测话题数（均为独立失败域）
  const [productData, setProductData] =
    useState<PaginatedData<ProductListItem> | null>(null);
  const [avgVisibility, setAvgVisibility] = useState<number | null>(null);
  const [topicCount, setTopicCount] = useState<number | null>(null);

  useEffect(() => {
    if (effectiveBrandId) {
      setLoading(true);
      setError("");
      getBrand(effectiveBrandId)
        .then(setData)
        .catch((err) =>
          setError(
            err instanceof Error ? err.message : "获取品牌详情失败",
          ),
        )
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [effectiveBrandId]);

  /** 名下产品（指标卡"产品数" + 底部列表共用一次请求）。 */
  useEffect(() => {
    if (!effectiveBrandId) {
      setProductData(null);
      return;
    }
    let cancelled = false;
    getProducts({ brand_id: effectiveBrandId, size: 50 })
      .then((res) => {
        if (!cancelled) setProductData(res);
      })
      .catch(() => {
        if (!cancelled) setProductData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveBrandId]);

  /** AI 可见性均值（接口失败 → null → 跳过该指标卡）。 */
  useEffect(() => {
    if (!effectiveBrandId) {
      setAvgVisibility(null);
      return;
    }
    let cancelled = false;
    getBrandAiVisibility(effectiveBrandId)
      .then((list) => {
        if (cancelled) return;
        if (list.length === 0) {
          setAvgVisibility(null);
          return;
        }
        const avg =
          list.reduce((sum, i) => sum + i.mention_rate, 0) / list.length;
        setAvgVisibility(avg);
      })
      .catch(() => {
        if (!cancelled) setAvgVisibility(null);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveBrandId]);

  /** 监测话题数（matrix 端点未就绪 → null → 跳过该指标卡）。 */
  useEffect(() => {
    if (!effectiveBrandId) {
      setTopicCount(null);
      return;
    }
    let cancelled = false;
    getVisibilityMatrix(effectiveBrandId, 7)
      .then((m) => {
        if (!cancelled) setTopicCount(m.topics?.length ?? null);
      })
      .catch(() => {
        if (!cancelled) setTopicCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveBrandId]);

  if (!effectiveBrandId) {
    return (
      <Box>
        <PageHeader
          title="品牌"
          breadcrumbs={[{ label: "资产管理" }, { label: "品牌" }]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先选择一个品牌"
              subtitle="使用页面顶部的品牌选择器，选择要查看的品牌"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <PageHeader title="品牌详情" />
        <Loading fullPage text="正在加载品牌详情..." />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <PageHeader title="品牌详情" />
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: 13, color: "error.main" }}>
              {error || "品牌不存在"}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const products = productData?.items ?? [];

  const productColumns: DataTableColumn<ProductListItem>[] = [
    {
      key: "name",
      label: "产品名",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
          {row.name}
        </Typography>
      ),
    },
    {
      key: "category",
      label: "类目",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.category || "—"}
        </Typography>
      ),
    },
    {
      key: "ai_visibility_rate",
      label: "AI 可见性",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 13, color: "text.primary" }}>
          {row.ai_visibility_rate !== null && row.ai_visibility_rate !== undefined
            ? row.ai_visibility_rate.toFixed(1)
            : "—"}
        </Typography>
      ),
    },
    {
      key: "ghs_score",
      label: "GHS",
      align: "center",
      render: (row) => <GhsRing score={row.ghs_score} size={36} thickness={4} />,
    },
  ];

  return (
    <Box>
      <PageHeader
        title={data.name}
        subtitle={data.brand_positioning || data.full_name || undefined}
        breadcrumbs={[
          { label: "资产管理" },
          { label: "品牌", path: "/brands" },
          { label: data.name },
        ]}
      />

      {/* ===== 指标卡（产品数恒显；AI 可见性均值/监测话题数取不到则跳过，不留假数据） ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="产品数"
            value={productData ? productData.total : "—"}
            subtitle={productData ? undefined : "产品接口未接入"}
            icon={<Inventory2Outlined />}
            iconBg="brand.50"
            iconColor="primary.main"
          />
        </Grid>
        {avgVisibility !== null && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="AI 可见性均值"
              value={avgVisibility.toFixed(1)}
              subtitle="全平台探测记录提及率均值"
              icon={<VisibilityOutlined />}
              iconBg="semanticBg.info"
              iconColor="info.main"
            />
          </Grid>
        )}
        {topicCount !== null && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="监测话题数"
              value={topicCount}
              subtitle="近 7 日全景矩阵话题"
              icon={<TopicOutlined />}
              iconBg="semanticBg.success"
              iconColor="success.main"
            />
          </Grid>
        )}
      </Grid>

      {/* ===== 基本信息（原三分组合并为一张 kv 卡，字段不变） ===== */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4">基本信息</Typography>

          <SectionLabel text="主体身份" />
          <Grid container spacing={2}>
            <FieldRow label="品牌名称" value={data.name} />
            <FieldRow label="品牌全称" value={data.full_name} />
            <FieldRow label="品牌 Logo" value={data.logo_url} />
            <FieldRow label="品牌定位" value={data.brand_positioning} />
          </Grid>

          <SectionLabel text="联系信息（继承公司 · 可覆盖）" />
          <Grid container spacing={2}>
            <FieldRow label="地址" value={data.address} />
            <FieldRow label="网站" value={data.website} />
            <FieldRow label="电话" value={data.phone} />
            <FieldRow label="邮箱" value={data.email} />
            <FieldRow
              label="官方链接"
              value={data.same_as?.length ? data.same_as.join("、") : undefined}
            />
          </Grid>

          <SectionLabel text="业务与内容" />
          <Grid container spacing={2}>
            <FieldRow label="品牌故事/理念" value={data.description} />
            <FieldRow label="品牌口号" value={data.brand_slogan} />
            <FieldRow label="目标人群" value={data.target_audience} />
            <FieldRow label="核心卖点（品牌级）" value={data.core_selling_points} />
          </Grid>

          <Typography
            sx={{
              fontSize: 11,
              color: "text.disabled",
              mt: 2,
              pt: 1.5,
              borderTop: "1px solid",
              borderColor: "lineSoft",
            }}
          >
            资产信息由运营方在资产 skill 侧录入维护，网站侧只读展示（PRD 1.2）。
          </Typography>
        </CardContent>
      </Card>

      {/* ===== 名下产品（行点击进 /products/:id） ===== */}
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="h4">名下产品</Typography>
            {productData && <Badge label={`${productData.total} 个`} />}
          </Box>
          {productData === null ? (
            <EmptyState
              text="产品列表暂未接入"
              source="产品资产来自资产录入 · 接口恢复后自动展示"
            />
          ) : (
            <DataTable
              columns={productColumns}
              data={products}
              showPagination={false}
              getRowId={(row) => row.id}
              emptyText="名下暂无产品"
              onRowClick={(row) => navigate(`/products/${row.id}`)}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
